import { useEffect } from "react";
import { Modal, Form, Select, Button } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import { useGetBranchesQuery } from "../../../services/branches";
import { useTransferClubMutation } from "../../../services/user";
import "./styles.scss";

interface ClubTransferUser {
  _id?:          string;
  name?:         string;
  phoneNumber?:  string;
  branch?:       string;
  branchIds?:    { _id?: string; name?: string }[];
}

interface ClubTransferModalProps {
  open:    boolean;
  user:    ClubTransferUser | null;
  onClose: () => void;
}

interface ClubTransferFormValues {
  branchId: string;
}

const ClubTransferModal = ({ open, user, onClose }: ClubTransferModalProps) => {
  const [form] = Form.useForm<ClubTransferFormValues>();
  const { data: branchesData } = useGetBranchesQuery();
  const [transferClub, { isLoading: transferring }] = useTransferClubMutation();

  const allBranches: any[] = (branchesData as any)?.branches || (branchesData as any)?.data || [];

  // Only branches the user ISN'T already in — this is a transfer to somewhere new.
  const currentBranchIds = new Set((user?.branchIds || []).map(b => b._id).filter(Boolean));
  const branches = allBranches.filter((b: any) => !currentBranchIds.has(b._id));

  const currentBranchName = user?.branchIds?.map(b => b.name).filter(Boolean).join(', ') || user?.branch || '-';

  useEffect(() => {
    if (!open) return;
    form.resetFields();
  }, [open, user, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async (values: ClubTransferFormValues) => {
    if (!user?._id) return;
    // The transfer API expects the branch's numeric `branchId`, not its Mongo `_id`.
    const branch = branches.find((b: any) => b._id === values.branchId);
    if (!branch) return;
    try {
      await transferClub({
        userId:    user._id,
        branchIds: [branch.branchId],
      }).unwrap();
      handleClose();
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={
        <span className="club-transfer-modal-title">
          <span className="club-transfer-modal-icon"><SwapOutlined /></span>
          Club Transfer
        </span>
      }
      footer={null}
      destroyOnClose
      width={480}
      centered
      className="club-transfer-modal"
    >
      <div className="club-transfer-modal-customer">
        <div className="club-transfer-modal-customer-item">
          <span className="label">Name</span>
          <span className="value">{user?.name || '-'}</span>
        </div>
        <div className="club-transfer-modal-customer-item">
          <span className="label">Mobile</span>
          <span className="value">{user?.phoneNumber || '-'}</span>
        </div>
        <div className="club-transfer-modal-customer-item">
          <span className="label">Current Branch</span>
          <span className="value">{currentBranchName}</span>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSubmit} className="club-transfer-form">
        <Form.Item name="branchId" label="Transfer To Branch" rules={[{ required: true, message: 'Please select a branch' }]}>
          <Select
            placeholder="Select branch"
            showSearch
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            options={branches.map((b: any) => ({ value: b._id, label: b.name }))}
            notFoundContent="No other branches available"
          />
        </Form.Item>

        <div className="club-transfer-modal-footer">
          <Button type="primary" htmlType="submit" loading={transferring} icon={<SwapOutlined />}>
            Transfer Club
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default ClubTransferModal;
